import React from 'react';
import {
  FaClock,
  FaPercentage,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFlask
} from 'react-icons/fa';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import i18n from '../../i18n';

const Details = ({
  packageSale,
  specialOffer,
  title,
  descriptionSale,
  description,
  salePrice,
  salePercent,
  offerDuration,
  price,
  image,
  testsSale,
  tests,
  timeLeft,
  startDate,
  endDate,
  packageData
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';

  // Format dates
  const formattedStartDate = startDate
    ? moment(startDate).format(isArabic ? 'YYYY MMM DD' : 'DD MMM YYYY')
    : null;

  const formattedEndDate = endDate
    ? moment(endDate).format(isArabic ? 'YYYY MMM DD' : 'DD MMM YYYY')
    : null;

  const handleBooking = () => {
    navigate('/package/booking', { state: packageData });
  };

  const hasPriceInfo = salePrice || price || salePercent;

  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-3 gap-6 p-8 rounded-lg shadow-lg 
      ${specialOffer ? 'bg-yellow-50' : 'bg-white'} animate-fadeIn relative`}
    >
      {/* Offer Badge */}
      <div className="absolute top-4 right-4 z-10">
        {packageSale && (
          <span className="bg-green-600 text-white text-xs font-semibold px-4 py-2 rounded-full">
            {t('offer.packageOffer')}
          </span>
        )}
        {specialOffer && (
          <span className="bg-yellow-600 text-white text-xs font-semibold px-4 py-2 rounded-full">
            {t('offer.specialOffer')}
          </span>
        )}
      </div>

      
      {/* ================= Image Section ================= */}
      {image && (
        <div className="w-full h-[280px] lg:h-full rounded-xl overflow-hidden shadow-md">
          <img
            src={image}
            alt={title || 'package-image'}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        </div>
      )}

      {/* ================= Pricing Section ================= */}
      {hasPriceInfo && (
        <div
          className={`flex flex-col justify-center items-center text-center p-6 rounded-lg border-l-4 shadow-md hover:shadow-xl
          ${specialOffer ? 'border-yellow-600' : 'border-secondColor'}`}
        >
          {(salePrice || price) && (
            <>
              <h3 className="text-2xl font-bold text-yellow-600 mb-2">
                <FaMoneyBillWave className="inline-block mr-2" />
                {t('details.currentPrice')}
              </h3>

              <div className="text-4xl font-bold text-primeColor mb-3">
                {salePrice || price} {t('currency')}
              </div>

              {salePrice && price && (
                <div className="text-lg text-gray-500 line-through">
                  {price} {t('currency')}
                </div>
              )}
            </>
          )}

          {salePercent && (
            <div className="flex items-center gap-2 text-green-600 mt-3 text-lg">
              <FaPercentage />
              {t('details.discount')} {salePercent}%
            </div>
          )}

          {timeLeft >= 1 && (
            <div className="flex items-center gap-2 text-red-500 mt-3">
              <FaClock />
              {t('details.timeLeft')}: {timeLeft} {t('days')}
            </div>
          )}

          {packageData && (
            <button
              onClick={handleBooking}
              className="mt-5 bg-secondColor text-white py-2 px-6 rounded shadow-lg transition-all duration-300 hover:bg-secondColor/90"
            >
              {t('packages.bookNow')}
            </button>
          )}
        </div>
      )}

      {/* ================= Details Section ================= */}
      <div className={`animate-fadeIn ${!hasPriceInfo ? 'lg:col-span-2' : ''}`}>
        <h2 className="text-3xl font-bold text-secondColor mb-4 border-b-2 border-secondColor pb-2">
          {t('details.details')}
        </h2>

        {(descriptionSale || description) && (
          <div className="bg-gray-50 p-4 rounded-lg shadow-sm border-l-4 border-secondColor mb-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              {packageSale ? descriptionSale : description}
            </p>
          </div>
        )}

        {/* Tests */}
        {tests && !packageData?.onSale && (
          <div className="flex items-center gap-2 mb-4">
            <FaFlask className="text-secondColor text-xl" />
            <span className="text-lg">
              {t('details.testsNumber')}:{' '}
              <b className="text-secondColor">{tests}</b>
            </span>
          </div>
        )}

        {testsSale && packageData?.onSale && (
          <div className="flex items-center gap-2 mb-4">
            <FaFlask className="text-secondColor text-xl" />
            <span className="text-lg">
              {t('details.testsNumberSale')}:{' '}
              <b className="text-secondColor">{testsSale}</b>
            </span>
          </div>
        )}

        {/* Dates */}
        {(startDate || endDate || offerDuration) && (
          <div className="flex flex-col gap-4 mt-6 bg-white p-4 rounded-lg shadow-sm border">
            {startDate && (
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-blue-500" />
                {t('details.start')}:
                <span className="font-medium">{formattedStartDate}</span>
              </div>
            )}

            {endDate && (
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-blue-500" />
                {t('details.end')}:
                <span className="font-medium">{formattedEndDate}</span>
              </div>
            )}

            {offerDuration && (
              <div className="flex items-center gap-2">
                <FaClock className="text-yellow-500" />
                {t('details.offerDuration')}:
                <span className="font-medium">
                  {offerDuration}{' '}
                  {offerDuration > 1 ? t('days') : t('day')}
                </span>
              </div>
            )}
          </div>
        )}

        {!hasPriceInfo && (
          <button
            onClick={handleBooking}
            className="mt-6 bg-secondColor text-white px-6 py-2 rounded shadow-lg hover:bg-secondColor/90 transition-all"
          >
            {t('packages.bookNow')}
          </button>
        )}
      </div>

      
    </div>
  );
};

export default Details;
